/**
 * Families, manufacturers and tags — the dropdown lists, and the item
 * migrations that renaming and deleting them imply.
 *
 * Functional spec: `_edit_family` / `_delete_family` / `_delete_tag` in
 * `qt_poc/settings_qt.py`.
 *
 * This replaced a single "edit the whole list" tags field, which was wrong in
 * a way that left no trace: **a list entry and the items carrying that value
 * are two different pieces of state.** Editing "Switches" to "Switching" in a
 * list field rewrote the dropdown and left every item still tagged
 * `Switches` — a value that no longer appeared in any list, so those items
 * simply stopped being findable by family. Nothing errored.
 *
 * So a rename here is always two operations: re-tag the items
 * (`POST /master-data/{list}/rename`), then rewrite the list. And a delete
 * asks what should happen to the items first, exactly as the desktop does:
 *
 *   * migrate them to another value — a rename under a different name;
 *   * or delete them, which the server refuses while any of them sits on a
 *     BOM line, because `bom_lines` has no cascade.
 *
 * Tags are the simple case: they have no per-item rename in the core (the
 * desktop has none either), so renaming a tag renames the list entry only,
 * and the dialog says so rather than implying more.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Badge, Button, Card, Group, Radio, ScrollArea, Select, Stack,
  Text, TextInput, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type { CountResult, MasterData } from '../api/types'
import { Modal } from '../components/AppModal'
import { confirmAction } from '../components/confirm'

/** What `GET /master-data/{list}/{value}/usage` answers. */
interface Usage {
  /** Items carrying this value — what a rename would re-tag. */
  items: number
  /** Of those, how many sit on a BOM line — what blocks deleting them. */
  in_use: number
}

type ListKey = 'families' | 'manufacturers' | 'tags'

const LISTS: { key: ListKey; label: string; singular: string; retags: boolean }[] = [
  { key: 'families', label: 'משפחות', singular: 'משפחה', retags: true },
  { key: 'manufacturers', label: 'יצרנים', singular: 'יצרן', retags: true },
  // No rename-across-items exists for tags in the core, only a strip-from-all
  // on delete. Claiming otherwise in the dialog would be a lie the user only
  // discovers later.
  { key: 'tags', label: 'תגיות', singular: 'תגית', retags: false },
]

/**
 * The importer's manufacturer aliases: raw name in a dealer file → the
 * canonical manufacturer it becomes.
 *
 * Listed here because an alias is the one piece of master data that **rewrites
 * incoming data** rather than describing it, on every future import, with no
 * mention in the preview. A wrong one is worse than no mapping: the catalog
 * ends up asserting something the source file never said. So it has to be
 * visible, and it has to be removable.
 *
 * They are created from the import preview, not here — the moment worth
 * recording is when someone corrects a real row.
 */
export function ManufacturerAliases({ disabled }: { disabled: boolean }) {
  const qc = useQueryClient()
  const aliases = useQuery({
    queryKey: ['aliases'],
    queryFn: () => api.get<{ manufacturers: Record<string, string> }>('/master-data/aliases'),
  })

  const remove = useMutation({
    mutationFn: (raw: string) =>
      api.delete<unknown>(`/master-data/aliases/manufacturers/${encodeURIComponent(raw)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['aliases'] })
      notifications.show({ message: 'הכינוי הוסר', color: 'green' })
    },
    onError: (err) => notifications.show({
      color: 'red',
      message: err instanceof ApiError ? err.message : 'ההסרה נכשלה',
    }),
  })

  const entries = Object.entries(aliases.data?.manufacturers ?? {})

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>כינויי יצרנים בייבוא</Title>
        <Badge size="sm" variant="light">{entries.length}</Badge>
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        שם יצרן כפי שהוא מופיע בקובץ הספק, והיצרן שאליו הוא מתורגם בכל ייבוא
        עתידי. נוצרים ממסך ייבוא הקטלוג, כשמתקנים שורה שדרשה תשומת לב.
      </Text>
      <ScrollArea h={200} type="auto">
        {aliases.isLoading && <Text size="sm" c="dimmed">טוען…</Text>}
        {!aliases.isLoading && entries.length === 0 && (
          <Text size="sm" c="dimmed">אין כינויים שמורים.</Text>
        )}
        <Stack gap={2}>
          {entries.map(([raw, canonical]) => (
            <Group key={raw} justify="space-between" wrap="nowrap" gap="xs">
              <Text size="sm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {raw} ← <b>{canonical}</b>
              </Text>
              {!disabled && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  loading={remove.isPending && remove.variables === raw}
                  onClick={() => {
                    void confirmAction({
                      title: 'הסרת כינוי',
                      message: `להסיר את הכינוי "${raw}"?`,
                      confirmLabel: 'הסר',
                    }).then((ok) => ok && remove.mutate(raw))
                  }}
                >
                  הסר
                </Button>
              )}
            </Group>
          ))}
        </Stack>
      </ScrollArea>
    </Card>
  )
}

export function MasterDataLists({ disabled }: { disabled: boolean }) {
  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })

  const values: Record<ListKey, string[]> = {
    families: master.data?.families ?? [],
    manufacturers: master.data?.manufacturers ?? [],
    tags: master.data?.tags ?? [],
  }

  return (
    <>
      {LISTS.map((list) => (
        <ListCard
          key={list.key}
          list={list}
          values={values[list.key]}
          disabled={disabled}
          loading={master.isLoading}
        />
      ))}
    </>
  )
}

function ListCard({
  list, values, disabled, loading,
}: {
  list: (typeof LISTS)[number]
  values: string[]
  disabled: boolean
  loading: boolean
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const sorted = useMemo(
    () => [...values].sort((a, b) => a.localeCompare(b, 'he')),
    [values],
  )

  useEffect(() => {
    if (selected && !values.includes(selected)) setSelected(null)
  }, [values, selected])

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>{list.label}</Title>
        <Badge size="sm" variant="light">{values.length}</Badge>
      </Group>

      <ScrollArea h={200} type="auto">
        <Stack gap={2}>
          {loading && <Text size="sm" c="dimmed">טוען…</Text>}
          {!loading && sorted.length === 0 && (
            <Text size="sm" c="dimmed">הרשימה ריקה.</Text>
          )}
          {sorted.map((value) => (
            <Button
              key={value}
              size="compact-sm"
              variant={selected === value ? 'light' : 'subtle'}
              justify="flex-start"
              fullWidth
              onClick={() => setSelected(value === selected ? null : value)}
            >
              {value}
            </Button>
          ))}
        </Stack>
      </ScrollArea>

      <Group gap="xs" mt="sm">
        <Button size="xs" disabled={disabled} onClick={() => setAdding(true)}>
          הוסף
        </Button>
        <Button
          size="xs"
          variant="default"
          disabled={disabled || !selected}
          onClick={() => setRenaming(selected)}
        >
          שנה שם
        </Button>
        <Button
          size="xs"
          variant="subtle"
          color="red"
          disabled={disabled || !selected}
          onClick={() => setDeleting(selected)}
        >
          מחק
        </Button>
      </Group>
      {disabled && (
        <Text size="xs" c="dimmed" mt={6}>
          הרשימות משפיעות על כל המסכים, ולכן ניתנות לשינוי למנהל בלבד.
        </Text>
      )}

      <AddValueModal
        list={list}
        existing={values}
        opened={adding}
        onClose={() => setAdding(false)}
      />
      <RenameValueModal
        list={list}
        existing={values}
        value={renaming}
        onClose={() => { setRenaming(null); setSelected(null) }}
      />
      <DeleteValueModal
        list={list}
        existing={values}
        value={deleting}
        onClose={() => { setDeleting(null); setSelected(null) }}
      />
    </Card>
  )
}

/** Rewrite one master list. The server de-duplicates and trims. */
function useReplaceList(key: ListKey) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (next: string[]) =>
      api.put<MasterData>(`/master-data/${key}`, { values: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['master-data'] })
      void qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

function AddValueModal({
  list, existing, opened, onClose,
}: {
  list: (typeof LISTS)[number]
  existing: string[]
  opened: boolean
  onClose: () => void
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const replace = useReplaceList(list.key)

  useEffect(() => { if (opened) { setValue(''); setError(null) } }, [opened])

  function submit() {
    const name = value.trim()
    if (!name) return setError('יש להזין שם.')
    if (existing.some((v) => v.toLowerCase() === name.toLowerCase()))
      return setError(`"${name}" כבר קיים ברשימה.`)
    replace.mutate([...existing, name], {
      onSuccess: () => {
        notifications.show({ message: `${list.singular} "${name}" נוספה`, color: 'green' })
        onClose()
      },
      onError: (err) =>
        setError(err instanceof ApiError ? err.message : 'ההוספה נכשלה.'),
    })
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`הוספת ${list.singular}`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput
          label="שם"
          required
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button loading={replace.isPending} onClick={submit}>הוסף</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function RenameValueModal({
  list, existing, value, onClose,
}: {
  list: (typeof LISTS)[number]
  existing: string[]
  value: string | null
  onClose: () => void
}) {
  const [next, setNext] = useState('')
  const [error, setError] = useState<string | null>(null)
  const replace = useReplaceList(list.key)
  const opened = value !== null

  useEffect(() => { if (opened) { setNext(value ?? ''); setError(null) } }, [opened, value])

  const usage = useUsage(list, value)

  const retag = useMutation({
    mutationFn: (payload: { old: string; new: string }) =>
      api.post<CountResult>(`/master-data/${list.key}/rename`, payload),
  })

  async function submit() {
    setError(null)
    const name = next.trim()
    if (!name) return setError('יש להזין שם.')
    if (name === value) return onClose()
    if (existing.some((v) => v.toLowerCase() === name.toLowerCase()))
      return setError(`"${name}" כבר קיים ברשימה.`)
    try {
      // Items first. If the rewrite of the list then fails, the items carry
      // the new value and the list still offers the old one — visible and
      // fixable. The other order loses the value entirely and strands them.
      let moved = 0
      if (list.retags) {
        moved = (await retag.mutateAsync({ old: value!, new: name })).count
      }
      await replace.mutateAsync(existing.map((v) => (v === value ? name : v)))
      notifications.show({
        color: 'green',
        message: moved
          ? `${list.singular} שונתה ל-"${name}" — עודכנו ${moved} פריטים`
          : `${list.singular} שונתה ל-"${name}"`,
      })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'שינוי השם נכשל.')
    }
  }

  const busy = retag.isPending || replace.isPending

  return (
    <Modal opened={opened} onClose={onClose} title={`שינוי שם — ${value ?? ''}`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        {list.retags ? (
          usage.data && usage.data.items > 0 ? (
            <Alert color="blue" variant="light">
              השינוי יעדכן {usage.data.items} פריטים.
            </Alert>
          ) : null
        ) : (
          <Alert color="yellow" variant="light">
            שינוי שם תגית מעדכן את הרשימה בלבד. פריטים שכבר נושאים את התגית
            הישנה ימשיכו לשאת אותה.
          </Alert>
        )}
        <TextInput
          label="שם חדש"
          required
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit() }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button loading={busy} onClick={() => void submit()}>שנה שם</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function DeleteValueModal({
  list, existing, value, onClose,
}: {
  list: (typeof LISTS)[number]
  existing: string[]
  value: string | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'migrate' | 'items'>('migrate')
  const [target, setTarget] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const replace = useReplaceList(list.key)
  const opened = value !== null

  const usage = useUsage(list, value)
  const others = existing.filter((v) => v !== value)

  useEffect(() => {
    if (opened) { setMode('migrate'); setTarget(others[0] ?? null); setError(null) }
    // `others` is derived from props that do not change while the modal is
    // open; keying on `value` keeps this from resetting the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, value])

  const migrate = useMutation({
    mutationFn: (payload: { old: string; new: string }) =>
      api.post<CountResult>(`/master-data/${list.key}/rename`, payload),
  })
  const deleteItems = useMutation({
    mutationFn: (name: string) =>
      api.delete<CountResult>(`/master-data/${list.key}/${encodeURIComponent(name)}/items`),
  })
  const stripTag = useMutation({
    mutationFn: (name: string) =>
      api.delete<CountResult>(`/master-data/tags/${encodeURIComponent(name)}`),
  })

  const carried = usage.data?.items ?? 0
  const locked = usage.data?.in_use ?? 0

  async function submit() {
    setError(null)
    try {
      let note = ''
      if (list.key === 'tags') {
        const { count } = await stripTag.mutateAsync(value!)
        note = count ? ` — הוסרה מ-${count} פריטים` : ''
      } else if (carried > 0 && mode === 'migrate') {
        if (!target) return setError('יש לבחור יעד להעברת הפריטים.')
        const { count } = await migrate.mutateAsync({ old: value!, new: target })
        note = ` — ${count} פריטים הועברו ל-"${target}"`
      } else if (carried > 0) {
        const { count } = await deleteItems.mutateAsync(value!)
        note = ` — ${count} פריטים נמחקו`
      }
      await replace.mutateAsync(others)
      void qc.invalidateQueries({ queryKey: ['items'] })
      notifications.show({
        color: 'green',
        message: `${list.singular} "${value}" נמחקה${note}`,
      })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'המחיקה נכשלה.')
    }
  }

  const busy = migrate.isPending || deleteItems.isPending ||
    stripTag.isPending || replace.isPending
  // The migrate path needs somewhere to migrate to.
  const blocked = list.key !== 'tags' && carried > 0 && mode === 'migrate' && !others.length

  return (
    <Modal opened={opened} onClose={onClose} title={`מחיקת ${list.singular} — ${value ?? ''}`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        {list.key === 'tags' ? (
          <Text size="sm">
            התגית תוסר גם מכל הפריטים שמשויכים אליה.
          </Text>
        ) : usage.isLoading ? (
          <Text size="sm" c="dimmed">בודק כמה פריטים מושפעים…</Text>
        ) : carried === 0 ? (
          <Text size="sm">אין פריטים המשויכים ל{list.singular} זו.</Text>
        ) : (
          <>
            <Text size="sm">
              {carried} פריטים משויכים ל{list.singular} זו. מה לעשות בהם?
            </Text>
            <Radio.Group value={mode} onChange={(v) => setMode(v as 'migrate' | 'items')}>
              <Stack gap="xs" mt="xs">
                <Radio value="migrate" label="להעביר אותם לערך אחר" />
                <Radio
                  value="items"
                  label="למחוק אותם לצמיתות"
                  disabled={locked > 0}
                  description={
                    locked > 0
                      ? `${locked} מהם משויכים ל-BOM קיים ולכן לא ניתן למחוק אותם.`
                      : 'פעולה זו אינה הפיכה.'
                  }
                />
              </Stack>
            </Radio.Group>
            {mode === 'migrate' && (
              others.length ? (
                <Select
                  label="להעביר אל"
                  data={others}
                  value={target}
                  onChange={setTarget}
                  searchable
                />
              ) : (
                <Alert color="yellow" variant="light">
                  אין ערך אחר להעביר אליו. הוסף {list.singular} נוספת תחילה,
                  או מחק את הפריטים.
                </Alert>
              )
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button
            color="red"
            loading={busy}
            disabled={blocked}
            onClick={() => void submit()}
          >
            מחק
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** Item counts for one value, fetched only while a dialog needs them. */
function useUsage(list: (typeof LISTS)[number], value: string | null) {
  return useQuery({
    queryKey: ['master-usage', list.key, value],
    queryFn: () =>
      api.get<Usage>(`/master-data/${list.key}/${encodeURIComponent(value!)}/usage`),
    // Tags have no usage endpoint — deletion strips them from every item
    // regardless, so there is no count to decide anything with.
    enabled: value !== null && list.retags,
    staleTime: 0,
  })
}
